import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { toast } from 'sonner'
import { queryClient, trpc } from '@/lib/trpc'
import { vendorLogoPath } from '@/lib/vendor-logo'
import { PersonnelSyncStatus } from '@/components/compliance/people/PersonnelSyncStatus'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import type { ConfigModalProps, IntegrationProvider } from '../types'

function HibobConfigModal({
  open,
  onOpenChange,
  integration,
  onComplete,
  workspaceId,
}: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false)
  const sync = useMutation(
    trpc.employee.syncPersonnel.mutationOptions({
      onSuccess: async () => {
        toast.success('HiBob personnel sync queued')
        await queryClient.invalidateQueries({
          queryKey: trpc.integrations.getHibobSyncStatus.queryKey(),
        })
      },
    })
  )
  const disconnect = useMutation(
    trpc.integrations.deleteIntegration.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.employee.listPersonnelProviders.queryKey(),
        })
        onComplete()
        onOpenChange(false)
      },
    })
  )
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>HiBob</DialogTitle>
          <DialogDescription>
            Sync the authorized employee roster, employment dates, departments
            and managers into People. HiBob controls which employees and fields
            Screenata can read.
          </DialogDescription>
        </DialogHeader>
        {integration && (
          <p className='text-muted-foreground text-sm'>
            Use Sync from HRIS in People to collect and inspect your directory.
            Employees outside the authorized audience are retained.
            Disconnecting stops future syncs and retains collected personnel.
            Revoke app access in HiBob separately.
          </p>
        )}
        {disconnect.error && (
          <p role='alert' className='text-destructive text-sm'>
            {disconnect.error.message}
          </p>
        )}
        {integration && <PersonnelSyncStatus />}
        {sync.error && (
          <p role='alert' className='text-destructive text-sm'>
            {sync.error.message}
          </p>
        )}
        <DialogFooter className='flex-wrap'>
          {integration && (
            <Button
              disabled={sync.isPending || connecting || disconnect.isPending}
              onClick={() => sync.mutate({ provider: 'hibob' })}
            >
              {sync.isPending ? 'Queuing…' : 'Sync personnel'}
            </Button>
          )}
          {integration && (
            <Button
              variant='destructive'
              disabled={disconnect.isPending || connecting}
              onClick={() => disconnect.mutate({ id: integration.id })}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          )}
          <Button
            variant='outline'
            disabled={connecting || disconnect.isPending}
            onClick={() => {
              setConnecting(true)
              void HibobProvider.handleConnect({
                workspaceId,
                onSuccess: onComplete,
                onFinish: () => setConnecting(false),
              }).catch(() => {})
            }}
          >
            {connecting ? 'Connecting…' : integration ? 'Reconnect' : 'Connect'}
          </Button>
          {integration && (
            <Button asChild>
              <Link
                to='/workspaces/$workspaceId/people'
                params={{ workspaceId }}
                onClick={() => onOpenChange(false)}
              >
                Open People
              </Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const HibobProvider: IntegrationProvider = {
  id: 'HiBob',
  name: 'HiBob',
  description: 'Sync authorized personnel and employment details into People',
  Icon: () => <img src={vendorLogoPath('hibob')} alt='' className='size-6' />,
  ConfigModal: HibobConfigModal,
  postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    {
      id: 'personnel',
      name: 'Personnel directory',
      description: 'Read employee lifecycle, dates, departments and managers',
      enabled: true,
    },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    try {
      const result = await queryClient
        .getMutationCache()
        .build(queryClient, trpc.integrations.initHibobAuth.mutationOptions())
        .execute(undefined)
      openOAuthConnectPopup({
        authUrl: result.authUrl,
        windowName: 'hibob-auth',
        provider: 'HiBob',
        onSuccess,
        onFinish,
        onError: (message) => toast.error(message),
        onPopupBlocked: () => toast.error('Allow popups to connect HiBob.'),
      })
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not start HiBob authorization'
      )
      onFinish?.('error')
      throw error
    }
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient
      .getMutationCache()
      .build(queryClient, trpc.integrations.completeHibobAuth.mutationOptions())
      .execute({ code, state })
    await queryClient.invalidateQueries({
      queryKey: trpc.employee.listPersonnelProviders.queryKey(),
    })
    return result
  },
  syncData: async () => {
    await queryClient
      .getMutationCache()
      .build(queryClient, trpc.employee.syncPersonnel.mutationOptions())
      .execute({ provider: 'hibob' })
    await queryClient.invalidateQueries({
      queryKey: trpc.integrations.getHibobSyncStatus.queryKey(),
    })
  },
}
